export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/d4XVzMoKouf7sFjWkoCoHm/APOMETRY-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/fpBiaEqCvAxejv84QuYaRK/HELENA_DUARTE_-_APOMETRIA.png',
};

export const pillars = ['Observe', 'Harmonize', 'Release', 'Protect', 'Integrate'];

export const feelings = [
  'emotional heaviness',
  'a sense of feeling weighed down',
  'thoughts they can’t seem to switch off',
  'lack of balance',
  'a feeling of being stuck',
  'a need to renew themselves',
  'difficulty maintaining a consistent spiritual practice',
];

export const searchHabits = [
  'They search for information.',
  'They watch videos.',
  'They read about chakras.',
  'They discover Apometry.',
  'They save rituals.',
  'They learn different techniques.',
];

export const beforeItems = [
  'You don’t know where to start',
  'You have information about Apometry but don’t know how to organize it',
  'You constantly switch between techniques',
  'You read about chakras, cleansing and protection without having a sequence',
  'You start a practice and then stop',
  'You don’t record what you experience',
  'You feel like you need to do something, but don’t know exactly what',
];

export const afterItems = [
  'You have a clear 7-day path',
  'You know what to do each day',
  'Your main practice takes approximately 15 minutes',
  'You follow an organized sequence',
  'You learn to observe your own experience',
  'You record your perceptions and reflections',
  'You finish with a routine you can use again',
];

export const situations = [
  {
    icon: 'compass',
    quote: 'I’m interested in Apometry, but I don’t know where to start.',
    text: 'You’ve found information about the 13 Laws, chakras, out-of-body work and different techniques, but everything seems too broad.',
  },
  {
    icon: 'clock',
    quote: 'I don’t have time to study for months.',
    text: 'You want to start practicing, but you can’t dedicate hours every day to studying theory.',
  },
  {
    icon: 'bookmark',
    quote: 'I have lots of techniques saved, but I don’t have a routine.',
    text: 'Every time you want to practice, you end up searching for another video or reading something different.',
  },
  {
    icon: 'weight',
    quote: 'I feel like I’m carrying too much and need to regain my balance.',
    text: 'You want to dedicate time to yourself, but you don’t have a structure that helps you get started.',
  },
  {
    icon: 'repeat',
    quote: 'I start practices but never manage to stay consistent.',
    text: 'The motivation appears for a few days and then disappears because you don’t have a defined path.',
  },
  {
    icon: 'book',
    quote: 'I want to learn Apometry in a practical way.',
    text: 'You don’t want to simply read theory. You want to understand how to organize a practice step by step.',
  },
] as const;

export const volumes = [
  {
    numeral: 'I',
    title: 'Prepare Your Practice',
    intro: 'Before you begin, you need to establish your starting point. I’ll guide you through preparing your space, your practice time, your intention, your state of presence and your initial journal entry.',
    includes: ['Preparation checklist', 'Intention exercise', 'Opening routine', 'Initial self-assessment', 'Getting-started guide', 'Centering practice'],
  },
  {
    numeral: 'II',
    title: 'Observe Before You Intervene',
    intro: 'One of the most important parts of any practice is learning how to observe. You’ll also find a simple initial assessment so you can compare your experience at the end of the 7 days.',
    includes: ['Self-observation', 'Conscious breathing', 'Sensation tracking', 'Emotional tracking', 'Thought observation', 'Pattern identification', 'Practice journal'],
  },
  {
    numeral: 'III',
    title: 'Harmonization & Chakras',
    intro: 'This is where the core practice begins — practices inspired by traditional Apometry energy work, along with visualization and attention-based tools.',
    includes: ['Practical introduction to the chakras', 'Breathing routine', 'Visualization', 'Centering', 'Harmonization', 'Practice sequence', 'Before-and-after journal', '15-minute protocol'],
  },
  {
    numeral: 'IV',
    title: 'Symbolic Cleansing & Release',
    intro: 'Designed to work with whatever you subjectively feel ready to let go of. The goal is to create space for personal reflection and practice. Not presented as a diagnosis or treatment for medical or psychological conditions.',
    includes: ['Visualization', 'Release', 'Reflective writing', 'Detachment', 'Renewal', 'Symbolic closure', 'Intention reset'],
  },
  {
    numeral: 'V',
    title: 'Protection & Closing',
    intro: 'A practice also needs a beginning and an ending. Here you’ll find a simple structure for closing your sessions.',
    includes: ['Protective preparation', 'Visualization', 'Grounding', 'Practice closure', 'Space organization', 'Post-practice routine', 'Closing checklist', '5-minute quick protocol'],
  },
];

export const days = [
  { day: 1, name: 'Prepare', text: 'Set your intention and prepare your space.' },
  { day: 2, name: 'Observe', text: 'Learn to record how you arrive at your practice.' },
  { day: 3, name: 'Harmonize', text: 'Complete your first structured harmonization sequence.' },
  { day: 4, name: 'Release', text: 'Work symbolically with what you want to let go of.' },
  { day: 5, name: 'Protect', text: 'Learn your closing and protection routine.' },
  { day: 6, name: 'Integrate', text: 'Observe what you’ve experienced throughout the previous days.' },
  { day: 7, name: 'Consolidate', text: 'Complete your review and create your routine for continuing.' },
];

export const extras = [
  { icon: 'practices', title: '21 Guided Practices', text: 'A library of short practices organized by purpose.' },
  { icon: 'checklist', title: '7D Apometry Checklists', text: 'So you never have to wonder what to do before, during or after each practice.' },
  { icon: 'map', title: 'Visual Chakra Map', text: 'A quick-reference guide to accompany your harmonization practices.' },
  {
    icon: 'journal',
    title: 'Observation Journal',
    text: 'Record how you felt before, which practice you completed, what you noticed, how you felt afterward and what you want to observe tomorrow.',
  },
  { icon: 'calendar', title: '7-Day Calendar', text: 'Check off each completed day and see your progress.' },
] as const;

export const resultStats = [
  { value: '7', label: 'days' },
  { value: '7', label: 'practices' },
  { value: '≈105', label: 'minutes' },
  { value: '1', label: 'structured routine' },
];

export const bonuses = [
  {
    number: 1,
    title: '21-Day Unblocking Journal™',
    text: 'Continue after completing the initial protocol. For 21 days, record your practices, perceptions, reflections and personal observations.',
    includes: ['21 tracking pages', '1–10 subjective rating scale', 'Reflection questions', 'Weekly review', '21-day closing exercise'],
    image: 'https://cdn.magicpatterns.com/uploads/qZyymTfiwhAHM3kcrVjVWY/BONUS_1_APOMETRY-EN.png',
  },
  {
    number: 2,
    title: '30 Harmonization Cards',
    text: 'When you don’t know which practice to do, simply choose a card. Each card includes Intention → Practice → Duration → Reflection. 30 options to keep your routine without improvising.',
    includes: ['30 illustrated cards', 'Intention for each card', 'Guided practice', 'Suggested duration', 'Reflection question'],
    image: 'https://cdn.magicpatterns.com/uploads/euvCfErtKsGjBYABftwfh4/BONUS_2_APOMETRY-EN.png',
  },
  {
    number: 3,
    title: '7D Home Harmonization Ritual',
    text: 'A specific protocol for working symbolically with your environment.',
    includes: ['Preparation', 'Space walkthrough', 'Intention', 'Visualization', 'Harmonization', 'Closing', 'Room-by-room checklist'],
    image: 'https://cdn.magicpatterns.com/uploads/nps74LtsX6CqnjVAUSfhUk/BONUS_3_APOMETRY-EN.png',
  },
  {
    number: 4,
    title: 'Protection & Closing Kit',
    text: 'A collection of quick routines to use before and after your practice.',
    includes: ['Opening', 'Preparation', 'Symbolic protection', 'Grounding', 'Closing', 'Transition back to your day'],
    image: 'https://cdn.magicpatterns.com/uploads/vv1rLP6QQ6nVBX6FbV1Kx8/BONUS_4_APOMETRY-EN.png',
  },
  {
    number: 5,
    title: '30-Day Spiritual Practice Calendar',
    text: 'Turn your first 7 days into a monthly practice. Each day includes a simple action: Observe → Breathe → Harmonize → Reflect → Record.',
    includes: ['30 daily actions', 'Daily practice sheet', 'Habit tracker', 'Weekly review', 'Practice themes'],
    image: 'https://cdn.magicpatterns.com/uploads/xqJV55pfwT2goTMiuopE12/BONUS_5_APOMETRY-EN.png',
  },
];

export const offerItems = [
  { icon: 'book', label: '7D Apometry Protocol™ — 6 Premium Volumes' },
  { icon: 'gift', label: '5 Exclusive Bonuses' },
  { icon: 'practices', label: '21 Guided Practices' },
  { icon: 'checklist', label: 'Checklists' },
  { icon: 'map', label: 'Chakra Map' },
  { icon: 'journal', label: 'Observation Journal' },
  { icon: 'calendar', label: '7-Day Calendar' },
] as const;

export const guaranteeItems = [
  'Open all 6 volumes',
  'Complete the practices',
  'Use the checklists',
  'Complete the calendar',
  'Try the bonuses',
  'Start your journal',
];

export const testimonials = [
  {
    name: 'Sarah M.',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/4b68aaf9-16bb-4467-ae82-adc671b1bddf.jpg',
    quote: 'I had dozens of saved videos about Apometry and never knew where to begin. Having one practice per day took all the guesswork away. I finished all 7 days — something I’ve never done with anything before.',
  },
  {
    name: 'Daniela R.',
    country: 'Canada',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/8b730697-0a94-4e7c-a819-8862c175f858.jpg',
    quote: '15 minutes was realistic for me, even with two kids at home. The checklists meant I never had to think about what came next. I just opened the day and followed it.',
  },
  {
    name: 'Margaret L.',
    country: 'United Kingdom',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/89379dfa-fff4-4e7e-b755-ed7131b3c4ca.jpg',
    quote: 'What I value most is the journal. Comparing day 1 with day 7 showed me how differently I was arriving at my practice. I’ve already started the 21-day journal.',
  },
  {
    name: 'James T.',
    country: 'Australia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/d428e766-5314-4926-b2eb-839dca175420.jpg',
    quote: 'The material is beautifully organized. Six volumes sounded like a lot, but each one is short and practical. I finally have a routine I can come back to.',
  },
];

export const faqs = [
  {
    q: 'Do I need to have studied Apometry before?',
    a: 'No. The 7D Apometry Protocol™ is designed as a practical introduction. You can start even if you’ve never taken an Apometry course before.',
  },
  {
    q: 'Is this a complete Apometry course?',
    a: 'No. It is a 7-day practical protocol inspired by Apometry principles and practices. A complete Apometry training requires a much broader course of study.',
  },
  {
    q: 'Will I learn all 13 Laws of Apometry?',
    a: 'The protocol introduces concepts related to Apometry practice, but it is not intended to replace an extensive training dedicated to the in-depth study of the 13 Laws and the full range of Apometry techniques.',
  },
  {
    q: 'How much time do I need each day?',
    a: 'The main practice is designed for approximately 15 minutes a day, for 7 days. That’s approximately 105 minutes in total.',
  },
  {
    q: 'What problem does the protocol address?',
    a: 'It is designed for people who subjectively experience feelings of heaviness, blockage, overwhelm or imbalance and want to develop a structured spiritual practice to work with those experiences. It does not diagnose spiritual, medical or psychological causes.',
  },
  {
    q: 'Do I need to be a medium?',
    a: 'No. You don’t need to consider yourself a medium or have any special ability to complete the proposed practices.',
  },
  {
    q: 'Do I need special materials?',
    a: 'No. The protocol is designed to be completed with basic materials and the resources included in the volumes themselves.',
  },
  {
    q: 'How will I receive the material?',
    a: 'After completing your purchase, you’ll receive digital access to the 6 volumes and 5 bonuses.',
  },
  {
    q: 'Can I download the files?',
    a: 'Yes. The digital materials are prepared for consultation and download according to the delivery conditions of the platform used.',
  },
  {
    q: 'How long will I have access?',
    a: 'You will have lifetime access, according to the offer terms.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The launch price is $27 USD. There is no monthly subscription.',
  },
  {
    q: 'What happens if it’s not right for me?',
    a: 'You have a 7-day guarantee. If, within that period, you decide that the material isn’t right for you, you can request a refund according to the purchase terms.',
  },
  {
    q: 'Does Apometry replace medical or psychological treatment?',
    a: 'No. The 7D Apometry Protocol™ is intended for spiritual practice and personal reflection. It should not be used to diagnose or treat physical or psychological conditions or replace care from qualified professionals.',
  },
];

export const buyers = [
  { name: 'Valentina R.', country: 'Colombia' },
  { name: 'María José G.', country: 'Mexico' },
  { name: 'Lucía F.', country: 'Argentina' },
  { name: 'Carmen S.', country: 'Spain' },
  { name: 'Camila P.', country: 'Chile' },
  { name: 'Andrea M.', country: 'Peru' },
  { name: 'Sofía L.', country: 'Uruguay' },
  { name: 'Gabriela T.', country: 'Ecuador' },
  { name: 'Daniela V.', country: 'Costa Rica' },
  { name: 'Isabel C.', country: 'Dominican Republic' },
  { name: 'Paola H.', country: 'Venezuela' },
  { name: 'Ana Lucía B.', country: 'Guatemala' },
  { name: 'Mariana O.', country: 'Puerto Rico' },
  { name: 'Fernanda Q.', country: 'Bolivia' },
  { name: 'Natalia D.', country: 'Paraguay' },
  { name: 'Carolina E.', country: 'Panama' },
];
